<template>
  <!--
    Student Journey V2 -- PRE-CHAT SETUP (00_SCOPE_AND_DESIGN_REFERENCE.md §18).

    Rebuilt from the RENDERED `borna-student-journey-v2.html` (sha256
    6ddb695a...2228), not from the setup it replaces. ONE page, no wizard
    steps: back-link, heading block, then a single white card holding the
    profile row, the mode panel, the grade and subject grids, the mode's own
    block, the "context ready" summary, the next-step note and the CTA.

    THE STUDY FIELD IS NOT CHOSEN HERE (human-closed decision, §18.3). It is
    read from the student's Profile (`GET /api/student/profile`), shown
    read-only in the profile row, and «ویرایش» navigates to Profile -- the only
    place it changes. This component never PATCHes the profile. Profile
    returns here and this component re-reads it on mount, so the value shown is
    always the current one.

    Contract preserved exactly: `submit` still emits
    `{ intent, grade, subject, topic }` (the store's `startSession` and
    `createSession(courseName, {})` are untouched -- only the course name ever
    reaches the backend), subject/course is REQUIRED and is a real catalog
    course, and loading / empty / error states block Continue (doc 11 §H).
    Everything else here (mode, grade label, stuck reason, proficiency, topic)
    is local presentation state.
  -->
  <div class="ai-tutor-setup-card" data-testid="ai-tutor-setup-step">
    <router-link
      :to="{ name: 'DashboardView' }"
      class="ai-tutor-setup__back"
      data-testid="ai-tutor-setup-back"
    >
      <student-home-icon name="arrow" :size="18" />
      {{ $t('aiTutorSetup.backToHome') }}
    </router-link>

    <div class="ai-tutor-setup__heading-block">
      <span class="ai-tutor-setup__icon" aria-hidden="true">
        <student-home-icon :name="modeIcon" :size="22" />
      </span>
      <div class="ai-tutor-setup__heading-text">
        <span class="ai-tutor-setup__eyebrow">{{ eyebrowText }}</span>
        <h1 class="ai-tutor-setup__heading">{{ headingText }}</h1>
        <p class="ai-tutor-setup__lead">{{ leadText }}</p>
      </div>
    </div>

    <p
      v-if="referenceError"
      class="ai-tutor-setup__error"
      role="alert"
      data-testid="ai-tutor-reference-error"
    >
      {{ $t('aiTutorSetup.contextStep.referenceError') }}
    </p>

    <form class="ai-tutor-setup__card" @submit.prevent="onSubmit">
      <!-- Profile row: name + the profile's study field, read-only. -->
      <div>
        <div class="ai-tutor-setup__profile" data-testid="ai-tutor-profile-row">
          <div class="ai-tutor-setup__profile-id">
            <student-home-icon name="user" :size="16" />
            <span>
              <strong v-if="profileName" data-testid="ai-tutor-profile-name">{{
                profileName
              }}</strong>
              <small v-if="studyFieldText" data-testid="ai-tutor-study-field">{{
                studyFieldText
              }}</small>
            </span>
          </div>
          <button
            type="button"
            class="ai-tutor-setup__profile-edit"
            data-testid="ai-tutor-edit-study-field"
            :aria-label="$t('aiTutorSetup.profile.editAria')"
            @click="onEditProfile"
          >
            <student-home-icon name="edit" :size="15" />
            <span class="ai-tutor-setup__profile-edit-label">{{
              $t('aiTutorSetup.profile.edit')
            }}</span>
          </button>
        </div>
        <p
          v-if="profileStatus === 'error'"
          class="ai-tutor-setup__error ai-tutor-setup__error--inline"
          role="alert"
          data-testid="ai-tutor-profile-error"
        >
          {{ $t('aiTutorSetup.profile.loadError') }}
          <button
            type="button"
            class="ai-tutor-setup__retry"
            data-testid="ai-tutor-profile-retry"
            @click="loadProfile"
          >
            {{ $t('aiTutorSetup.contextStep.retryButton') }}
          </button>
        </p>
        <p
          v-else-if="studyFieldMissing"
          class="ai-tutor-setup__hint ai-tutor-setup__hint--inline"
          data-testid="ai-tutor-study-field-missing"
        >
          {{ $t('aiTutorSetup.profile.fieldMissingHint') }}
        </p>
        <!-- P42-08: «هنوز انتخاب نکرده‌ام» is a valid saved answer, but no
             course is scoped to it, so it asks for a real رشته instead of
             sending a course request that can only come back empty. -->
        <p
          v-else-if="studyFieldUndecided"
          class="ai-tutor-setup__hint ai-tutor-setup__hint--inline"
          data-testid="ai-tutor-study-field-undecided"
        >
          {{ $t('aiTutorSetup.profile.fieldUndecidedHint') }}
        </p>
      </div>

      <!-- Mode panel -->
      <div class="ai-tutor-setup__intent-panel">
        <div class="ai-tutor-setup__intent-head">
          <student-home-icon name="spark" :size="22" />
          <div>
            <strong>{{ $t('aiTutorSetup.intentPanel.heading') }}</strong>
            <span>{{ $t('aiTutorSetup.intentPanel.lead') }}</span>
          </div>
        </div>
        <div class="ai-tutor-setup__intent-pills" role="group">
          <button
            v-for="option in INTENT_OPTIONS"
            :key="option"
            type="button"
            class="ai-tutor-setup__intent-pill"
            :class="{
              'ai-tutor-setup__intent-pill--selected': intent === option,
            }"
            :aria-pressed="intent === option ? 'true' : 'false'"
            :data-testid="`ai-tutor-intent-option-${option}`"
            @click="onIntentInput(option)"
          >
            {{ $t(`aiTutorSetup.intentStep.options.${option}.pillLabel`) }}
          </button>
        </div>
      </div>

      <!-- Grade -->
      <fieldset
        v-if="gradeOptions.length"
        class="ai-tutor-setup__field"
        data-testid="ai-tutor-grade"
      >
        <legend>{{ $t('aiTutorSetup.contextStep.gradeLabel') }}</legend>
        <setup-choice-grid
          variant="grade"
          testid-prefix="ai-tutor-grade-option"
          :options="gradeOptions"
          :value="grade"
          @select="onGradeInput"
        />
      </fieldset>

      <!-- Subject: the real catalog courses for (stage, grade, profile field) -->
      <fieldset
        v-if="
          readyForCourses && (courses.length || loadingCourses || coursesError)
        "
        class="ai-tutor-setup__field"
        data-testid="ai-tutor-subject"
        :aria-busy="loadingCourses ? 'true' : 'false'"
      >
        <legend>{{ $t('aiTutorSetup.contextStep.subjectLabel') }}</legend>
        <!-- SX-03B: courses without AI Tutor availability stay visible,
             badged «به‌زودی», reachable, never selectable; activating one
             opens the shared Coming Soon dialog (no session, no enrollment,
             no Synapse call). -->
        <setup-choice-grid
          v-if="courses.length"
          variant="subject"
          testid-prefix="ai-tutor-subject-option"
          :options="courseOptions"
          :value="subject"
          :unavailable-label="$t('studentComingSoon.badge')"
          @select="onSubjectInput"
          @unavailable="onSubjectUnavailable"
        />
        <p
          v-else-if="loadingCourses"
          class="ai-tutor-setup__hint"
          data-testid="ai-tutor-courses-loading"
        >
          {{ $t('aiTutorSetup.contextStep.coursesLoading') }}
        </p>
        <div v-if="coursesError" class="ai-tutor-setup__error-block">
          <p
            class="ai-tutor-setup__error"
            role="alert"
            data-testid="ai-tutor-courses-error"
          >
            {{ $t('aiTutorSetup.contextStep.coursesError') }}
          </p>
          <button
            type="button"
            class="ai-tutor-setup__retry"
            data-testid="ai-tutor-courses-retry"
            @click="fetchCourses"
          >
            {{ $t('aiTutorSetup.contextStep.retryButton') }}
          </button>
        </div>
      </fieldset>

      <!-- Troubleshoot: where exactly are you stuck -->
      <fieldset
        v-if="intent === 'troubleshoot'"
        class="ai-tutor-setup__field"
        data-testid="ai-tutor-stuck-reason"
      >
        <legend>{{ $t('aiTutorSetup.contextStep.stuckReasonLabel') }}</legend>
        <setup-choice-grid
          variant="choice"
          testid-prefix="ai-tutor-stuck-option"
          :options="stuckReasonOptions"
          :value="stuckReason"
          @select="stuckReason = $event"
        />
      </fieldset>

      <!-- Learn: topic panel, then how well do you know it -->
      <template v-else-if="intent === 'learn'">
        <div
          class="ai-tutor-setup__topic"
          data-testid="ai-tutor-topic"
          :class="{ 'ai-tutor-setup__topic--busy': !subject }"
        >
          <div class="ai-tutor-setup__topic-head">
            <div>
              <span>{{
                $t('aiTutorSetup.contextStep.topicPanel.heading')
              }}</span>
              <small>{{
                $t('aiTutorSetup.contextStep.topicPanel.lead')
              }}</small>
            </div>
            <span v-if="topicChipText" class="ai-tutor-setup__topic-chip">{{
              topicChipText
            }}</span>
          </div>
          <div class="ai-tutor-setup__topic-grid">
            <label
              class="ai-tutor-setup__topic-card"
              :class="{
                'ai-tutor-setup__topic-card--active': topicMode === 'select',
              }"
            >
              <span class="ai-tutor-setup__topic-kicker">{{
                $t('aiTutorSetup.contextStep.topicPanel.selectKicker')
              }}</span>
              <strong>{{
                $t('aiTutorSetup.contextStep.topicPanel.selectTitle')
              }}</strong>
              <small>{{
                $t('aiTutorSetup.contextStep.topicPanel.selectLead')
              }}</small>
              <select
                v-model="suggestedTopic"
                class="ai-tutor-setup__topic-control"
                data-testid="ai-tutor-topic-select"
                @change="onSuggestedTopicChange"
              >
                <option value="">{{
                  $t('aiTutorSetup.contextStep.topicPanel.selectPlaceholder')
                }}</option>
                <template v-if="topicOptionGroups">
                  <optgroup
                    v-for="group in topicOptionGroups"
                    :key="group.label"
                    :label="group.label"
                  >
                    <option
                      v-for="option in group.options"
                      :key="option.value"
                      :value="option.value"
                      >{{ option.label }}</option
                    >
                  </optgroup>
                </template>
                <template v-else>
                  <option
                    v-for="option in topicOptions"
                    :key="option.value"
                    :value="option.value"
                    >{{ option.label }}</option
                  >
                </template>
              </select>
            </label>
            <div
              class="ai-tutor-setup__topic-card"
              :class="{
                'ai-tutor-setup__topic-card--active': topicMode === 'custom',
              }"
            >
              <span class="ai-tutor-setup__topic-kicker">{{
                $t('aiTutorSetup.contextStep.topicPanel.customKicker')
              }}</span>
              <strong>{{
                $t('aiTutorSetup.contextStep.topicPanel.customTitle')
              }}</strong>
              <small>{{
                $t('aiTutorSetup.contextStep.topicPanel.customLead')
              }}</small>
              <input
                v-if="topicMode === 'custom'"
                ref="customTopicInput"
                v-model="customTopic"
                type="text"
                class="ai-tutor-setup__topic-control"
                data-testid="ai-tutor-topic-custom-input"
                :placeholder="
                  $t('aiTutorSetup.contextStep.topicPanel.customPlaceholder')
                "
                @keydown.enter="onCustomTopicEnter"
              />
              <button
                v-else
                type="button"
                class="ai-tutor-setup__topic-toggle"
                data-testid="ai-tutor-topic-custom-toggle"
                @click="onCustomTopicToggle"
              >
                {{ $t('aiTutorSetup.contextStep.topicPanel.customToggle') }}
              </button>
            </div>
          </div>
        </div>

        <fieldset
          class="ai-tutor-setup__field"
          data-testid="ai-tutor-proficiency"
        >
          <legend>{{ $t('aiTutorSetup.contextStep.proficiencyLabel') }}</legend>
          <setup-choice-grid
            variant="choice"
            testid-prefix="ai-tutor-proficiency-option"
            :options="proficiencyOptions"
            :value="proficiency"
            @select="proficiency = $event"
          />
          <p
            class="ai-tutor-setup__level-note"
            data-testid="ai-tutor-proficiency-note"
          >
            <student-home-icon name="spark" :size="16" />
            <span>{{
              $t(`aiTutorSetup.contextStep.proficiencyNotes.${proficiency}`)
            }}</span>
          </p>
        </fieldset>

        <!-- STUDENT-UX-02 / UX2-01 (doc 30 §6, D-1): the OPTIONAL initial
             question. It is the student's exact first message to the Tutor
             (sent once, through the ordinary send path, by the chat view) --
             never synthesized from the topic, never reworded, never part of
             the session snapshot. Optional: `canContinue` ignores it. -->
        <label
          class="ai-tutor-setup__field ai-tutor-setup__question"
          data-testid="ai-tutor-initial-question-field"
        >
          <span class="ai-tutor-setup__question-label">{{
            $t('aiTutorSetup.contextStep.initialQuestion.label')
          }}</span>
          <textarea
            v-model="initialQuestion"
            class="ai-tutor-setup__topic-control ai-tutor-setup__question-input"
            data-testid="ai-tutor-initial-question"
            rows="3"
            :maxlength="INITIAL_QUESTION_MAX_LENGTH"
            :placeholder="
              $t('aiTutorSetup.contextStep.initialQuestion.placeholder')
            "
          />
          <small class="ai-tutor-setup__question-hint">{{
            $t('aiTutorSetup.contextStep.initialQuestion.hint')
          }}</small>
        </label>
      </template>

      <!-- Context summary -->
      <div
        v-if="previewState"
        class="ai-tutor-setup__preview"
        :class="{
          'ai-tutor-setup__preview--blocked': previewState !== 'ready',
        }"
        data-testid="ai-tutor-ready-banner"
      >
        <student-home-icon name="spark" :size="22" />
        <div>
          <strong>{{ previewTitle }}</strong>
          <span>{{ previewText }}</span>
        </div>
      </div>

      <p class="ai-tutor-setup__next-note">
        <student-home-icon name="chat" :size="18" />
        <span>{{ $t('aiTutorSetup.contextStep.readyBannerNote') }}</span>
      </p>

      <p
        v-if="showGenericSubmitError"
        class="ai-tutor-setup__error"
        role="alert"
        data-testid="ai-tutor-submit-error"
      >
        {{ submitErrorText }}
      </p>

      <button
        type="submit"
        class="ai-tutor-setup__submit"
        data-testid="ai-tutor-setup-submit"
        :disabled="!canContinue || starting"
      >
        {{
          starting
            ? $t('aiTutorSetup.contextStep.startingButton')
            : $t('aiTutorSetup.contextStep.startButton')
        }}
        <student-home-icon name="arrow" :size="18" />
      </button>

      <!-- V2's "this choice is not active yet" note. Driven by the server
           (SX-03B): the chosen course has no AI Tutor availability or was
           refused by createSession's fail-closed mapping check; no course of
           this stage/grade/field is available yet; or the catalog is empty.
           The copy is data-driven -- it never claims which subjects are
           active. -->
      <p
        v-if="showAvailabilityNote"
        class="ai-tutor-setup__availability"
        role="status"
        :data-testid="availabilityNoteTestId"
      >
        <student-home-icon name="spark" :size="18" />
        <span>{{ availabilityNoteText }}</span>
      </p>
    </form>
  </div>
</template>

<script>
import StudentService from '@/services/StudentService'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import SetupChoiceGrid from './SetupChoiceGrid.vue'
import { topicSuggestionsFor } from './topicSuggestions'
// The aiTutor module's collaborators (its api / adapter), the same seam the
// chat's Board sheet uses -- test doubles replace them, never `jest.mock`.
import { collaborators as aiTutorCollaborators } from '@/store/modules/aiTutor'

// 36 TC-2 (doc 36/01 §3): what Subway answers when a chapter picked from the
// course outline cannot be used. Each has its own line in the Setup.
const TOPIC_NOT_IN_OUTLINE = 'AI_TUTOR_TOPIC_NOT_IN_OUTLINE'
const OUTLINE_UNAVAILABLE = 'AI_TUTOR_OUTLINE_UNAVAILABLE'

const INTENT_OPTIONS = ['troubleshoot', 'review', 'learn']
const DEFAULT_INTENT = 'troubleshoot'
// UX2-01: the optional initial question is a first chat message, not an
// essay. The chat composer imposes no length limit of its own, so this is
// the only guard; 2000 characters is far above any question a student types.
const INITIAL_QUESTION_MAX_LENGTH = 2000
const MODE_ICONS = { troubleshoot: 'chat', review: 'check', learn: 'book' }
const STUCK_REASON_OPTIONS = ['misunderstand', 'dontKnowStart', 'stuckStep']
const PROFICIENCY_OPTIONS = ['none', 'little', 'review']
// SX-03B (doc 22 §6.3): the real fail-closed code Subway sends when a course
// has no Synapse mapping, plus the historical code kept for compatibility.
// Either turns the chosen course into the Coming Soon state, never the
// generic failure line.
const NOT_PROVISIONED_CODES = [
  'SYNAPSE_COURSE_MAPPING_MISSING',
  'AI_TUTOR_COURSE_NOT_PROVISIONED',
]

// SX-03B: a course is available unless the catalog says `aiTutorAvailable:
// false`. A MISSING field is treated as available (deploy skew between the
// Subway contract and this bundle, and older fixtures).
const isCourseAvailable = course => !course || course.aiTutorAvailable !== false

// Phase-1 catalog (doc 11 §K, `WP-02C-CATALOG`) is single-stage. The student's
// own stage comes from the profile; this is only the fallback when a profile
// carries none, so the reference list still resolves to something real.
const PREFERRED_STAGE = 'highSchool'

// P42-08: the profile's placeholder answer «هنوز انتخاب نکرده‌ام». It may be
// saved, but it is not a real رشته: no catalog course is scoped to it.
const UNDECIDED_STUDY_FIELD = 'undecided'

export default {
  name: 'SetupCard',
  components: { StudentHomeIcon, SetupChoiceGrid },
  props: {
    starting: { type: Boolean, default: false },
    submitError: { type: Object, default: null },
    initialIntent: { type: String, default: '' },
    initialGrade: { type: String, default: '' },
    initialSubject: { type: String, default: '' },
    initialTopic: { type: String, default: '' },
  },
  data() {
    const restoredTopic = this.initialTopic || ''
    const isSuggestion = topicSuggestionsFor(this.initialSubject).includes(
      restoredTopic
    )
    return {
      INTENT_OPTIONS,
      INITIAL_QUESTION_MAX_LENGTH,
      // UX2-01: the optional first message (Learn only); trimmed on submit.
      initialQuestion: '',
      stages: [],
      loadingReference: true,
      referenceError: false,
      // 'loading' | 'loaded' | 'error' -- the student's Profile, the ONLY
      // source of the study field (and of the default stage / grade).
      profile: null,
      profileStatus: 'loading',
      intent: INTENT_OPTIONS.includes(this.initialIntent)
        ? this.initialIntent
        : DEFAULT_INTENT,
      grade: this.initialGrade,
      courses: [],
      loadingCourses: false,
      coursesError: false,
      subject: this.initialSubject,
      // Local-only presentation state (never in the emitted payload except
      // `topic`).
      stuckReason: 'stuckStep',
      proficiency: 'none',
      // '' (neither card active, as the reference opens) | 'select' | 'custom'
      topicMode: restoredTopic ? (isSuggestion ? 'select' : 'custom') : '',
      suggestedTopic: isSuggestion ? restoredTopic : '',
      customTopic: restoredTopic && !isSuggestion ? restoredTopic : '',
      // 36 TC-2: the chapters of the chosen course's outline (Subway, per
      // Student, never cached), for the course named in `outlineCourse`.
      // Empty -> the static suggestions, exactly as before.
      outlineChapters: [],
      outlineCourse: '',
      outlineSeq: 0,
      // A topic restored on retry that may be an outline chapter title: it is
      // re-selected once that course's outline arrives.
      pendingRestoredTopic: restoredTopic,
      // The course `createSession` last refused as not provisioned.
      lastSubmittedSubject: '',
      unavailableSubject: '',
      // SX-03B: courses the server refused this session (stale-cache 409
      // safety net) -- badged like a catalog-unavailable course.
      refusedSubjects: [],
    }
  },
  computed: {
    // The student's own stage; the preferred one only when the profile has
    // none (or the reference does not know it). Empty until both loads settle.
    stage() {
      if (this.loadingReference || this.profileStatus === 'loading') return ''
      if (!this.stages.length) return ''
      const own =
        this.profile && this.profile.educationStage
          ? this.stages.find(s => s.name === this.profile.educationStage.name)
          : null
      if (own) return own.name
      const preferred = this.stages.find(s => s.name === PREFERRED_STAGE)
      return (preferred || this.stages[0]).name
    },
    selectedStage() {
      return this.stages.find(s => s.name === this.stage) || null
    },
    availableGrades() {
      return this.selectedStage ? this.selectedStage.grades || [] : []
    },
    availableStudyFields() {
      return this.selectedStage ? this.selectedStage.studyFields || [] : []
    },
    gradeOptions() {
      return this.availableGrades.map(g => ({
        value: g.name,
        label: g.displayName || g.name,
      }))
    },
    gradeLabel() {
      const hit = this.gradeOptions.find(o => o.value === this.grade)
      return hit ? hit.label : ''
    },
    studyFieldName() {
      return (
        (this.profile &&
          this.profile.studyField &&
          this.profile.studyField.name) ||
        ''
      )
    },
    studyFieldRequired() {
      return this.availableStudyFields.length > 0
    },
    studyFieldMissing() {
      return (
        this.profileStatus === 'loaded' &&
        this.studyFieldRequired &&
        !this.studyFieldName
      )
    },
    studyFieldUndecided() {
      return (
        this.profileStatus === 'loaded' &&
        this.studyFieldRequired &&
        this.studyFieldName === UNDECIDED_STUDY_FIELD
      )
    },
    profileName() {
      if (!this.profile) return ''
      return [this.profile.firstName, this.profile.lastName]
        .filter(Boolean)
        .join(' ')
    },
    studyFieldText() {
      if (this.profileStatus === 'loading') {
        return this.$t('aiTutorSetup.profile.loading')
      }
      if (this.profileStatus === 'error') {
        return this.$t('aiTutorSetup.profile.studyFieldMissing')
      }
      const display =
        this.profile && this.profile.studyField
          ? this.profile.studyField.displayName || this.profile.studyField.name
          : ''
      if (display) {
        return this.$t('aiTutorSetup.profile.studyField', { field: display })
      }
      // TR-021: a stage with no study fields (data-driven, from the reference
      // payload) has nothing "not yet specified". An unresolved stage keeps
      // the line, exactly as before.
      if (this.selectedStage && !this.studyFieldRequired) return ''
      return this.$t('aiTutorSetup.profile.studyFieldMissing')
    },
    readyForCourses() {
      if (!this.stage || !this.grade) return false
      if (this.profileStatus !== 'loaded') return false
      if (this.studyFieldRequired && !this.studyFieldName) return false
      // P42-08: a placeholder is not a usable رشته for the Tutor.
      if (this.studyFieldUndecided) return false
      return true
    },
    // Changes exactly when the catalog query would: the ONE watcher key.
    courseQueryKey() {
      return this.readyForCourses
        ? [this.stage, this.grade, this.studyFieldName].join('|')
        : ''
    },
    courseOptions() {
      return this.courses.map(c => ({
        value: c.name,
        label: c.displayName || c.name,
        unavailable:
          !isCourseAvailable(c) || this.refusedSubjects.includes(c.name),
      }))
    },
    // SX-03B: the courses a session may actually be started on.
    availableCourses() {
      return this.courses.filter(
        c => isCourseAvailable(c) && !this.refusedSubjects.includes(c.name)
      )
    },
    // The catalog has courses for this scope but none is available yet.
    noCourseAvailable() {
      return (
        this.readyForCourses &&
        !this.loadingCourses &&
        !this.coursesError &&
        this.courses.length > 0 &&
        this.availableCourses.length === 0
      )
    },
    subjectLabel() {
      const hit = this.courseOptions.find(o => o.value === this.subject)
      return hit ? hit.label : ''
    },
    catalogEmpty() {
      return (
        this.readyForCourses &&
        !this.loadingCourses &&
        !this.coursesError &&
        this.courses.length === 0
      )
    },
    subjectUnavailable() {
      return (
        Boolean(this.subject) &&
        (this.subject === this.unavailableSubject ||
          this.refusedSubjects.includes(this.subject))
      )
    },
    showAvailabilityNote() {
      return (
        this.catalogEmpty || this.noCourseAvailable || this.subjectUnavailable
      )
    },
    availabilityNoteTestId() {
      if (this.catalogEmpty) return 'ai-tutor-subject-empty'
      if (this.noCourseAvailable) return 'ai-tutor-no-course-available'
      return 'ai-tutor-availability-note'
    },
    // Data-driven copy: "this course" when a chosen course is unavailable,
    // "no course of this grade" when nothing in the scope is available yet.
    availabilityNoteText() {
      if (this.subjectUnavailable) {
        return this.$t('aiTutorSetup.contextStep.availabilityNoteCourse')
      }
      return this.$t('aiTutorSetup.contextStep.availabilityNoteNone')
    },
    notProvisionedError() {
      return (
        Boolean(this.submitError) &&
        NOT_PROVISIONED_CODES.includes(this.submitError.code)
      )
    },
    // A not-provisioned refusal is shown as V2's "not active yet" state, not
    // as the generic failure line.
    showGenericSubmitError() {
      return Boolean(this.submitError) && !this.notProvisionedError
    },
    stuckReasonOptions() {
      return STUCK_REASON_OPTIONS.map(o => ({
        value: o,
        label: this.$t(`aiTutorSetup.contextStep.stuckReasonOptions.${o}`),
      }))
    },
    proficiencyOptions() {
      return PROFICIENCY_OPTIONS.map(o => ({
        value: o,
        label: this.$t(`aiTutorSetup.contextStep.proficiencyOptions.${o}`),
      }))
    },
    // 36 TC-2: the outline's chapters, only for the course they were read for.
    outlineActive() {
      return (
        this.outlineChapters.length > 0 && this.outlineCourse === this.subject
      )
    },
    // The topic select's options: the course outline's chapters (value = the
    // node id, label = the provider title) when Subway listed any, else the
    // static suggestions (value = label = the text) exactly as before.
    topicOptions() {
      if (this.outlineActive) {
        return this.outlineChapters.map(chapter => ({
          value: chapter.nodeId,
          label: chapter.title,
          group: chapter.group,
        }))
      }
      return topicSuggestionsFor(this.subject).map(text => ({
        value: text,
        label: text,
        group: null,
      }))
    },
    // Several sources with chapters -> one <optgroup> per source title.
    topicOptionGroups() {
      if (!this.topicOptions.some(option => option.group)) return null
      const groups = []
      this.topicOptions.forEach(option => {
        const label = option.group || ''
        let group = groups.find(entry => entry.label === label)
        if (!group) {
          group = { label, options: [] }
          groups.push(group)
        }
        group.options.push(option)
      })
      return groups
    },
    selectedTopicOption() {
      return (
        this.topicOptions.find(
          option => option.value === this.suggestedTopic
        ) || null
      )
    },
    // A chapter picked from the outline names its node (Subway checks it);
    // a static suggestion or a typed topic names none.
    topicNodeId() {
      return this.topicMode === 'select' &&
        this.outlineActive &&
        this.selectedTopicOption
        ? this.selectedTopicOption.value
        : null
    },
    submitErrorText() {
      const code = this.submitError && this.submitError.code
      if (code === TOPIC_NOT_IN_OUTLINE) {
        return this.$t('aiTutorSetup.contextStep.topicPanel.chapterRefused')
      }
      if (code === OUTLINE_UNAVAILABLE) {
        return this.$t('aiTutorSetup.contextStep.topicPanel.chapterUnavailable')
      }
      return this.$t('aiTutorSetup.contextStep.submitError')
    },
    topicChipText() {
      return this.gradeLabel && this.subjectLabel
        ? `${this.gradeLabel} · ${this.subjectLabel}`
        : ''
    },
    topicValue() {
      if (this.topicMode === 'custom') return (this.customTopic || '').trim()
      return this.selectedTopicOption ? this.selectedTopicOption.label : ''
    },
    modeIcon() {
      return MODE_ICONS[this.intent]
    },
    modeLabel() {
      return this.$t(`aiTutorSetup.intentStep.options.${this.intent}.pillLabel`)
    },
    eyebrowText() {
      return this.modeLabel
    },
    headingText() {
      return this.$t(`aiTutorSetup.intentStep.options.${this.intent}.heading`)
    },
    leadText() {
      return this.$t(`aiTutorSetup.intentStep.options.${this.intent}.lead`)
    },
    // «ریاضی دهم» -- also the label the chat's context bar shows.
    contextCourseLabel() {
      if (this.subjectLabel && this.gradeLabel) {
        return `${this.subjectLabel} ${this.gradeLabel}`
      }
      return this.subjectLabel || this.gradeLabel || ''
    },
    trailingSummary() {
      if (this.intent === 'troubleshoot' && this.stuckReason) {
        return this.$t(
          `aiTutorSetup.contextStep.stuckReasonOptions.${this.stuckReason}`
        )
      }
      if (this.intent === 'learn' && this.proficiency) {
        return `${this.$t(
          'aiTutorSetup.contextStep.proficiencySummaryLabel'
        )}: ${this.$t(
          `aiTutorSetup.contextStep.proficiencyOptions.${this.proficiency}`
        )}`
      }
      return ''
    },
    // null (nothing to summarise yet) | 'ready' | 'blocked' | 'unavailable'
    previewState() {
      if (!this.courses.length) return null
      // SX-03B: nothing selectable in this scope -- V2's blocked summary,
      // truthfully, instead of an empty card.
      if (!this.subject) return this.noCourseAvailable ? 'unavailable' : null
      if (this.subjectUnavailable) return 'unavailable'
      if (this.intent === 'learn' && !this.topicValue) return 'blocked'
      return 'ready'
    },
    previewTitle() {
      if (this.previewState === 'unavailable') {
        return this.$t('aiTutorSetup.contextStep.readyUnavailable')
      }
      if (this.previewState === 'blocked') {
        return this.$t('aiTutorSetup.contextStep.readyBlockedTopic')
      }
      return this.$t('aiTutorSetup.contextStep.readyBannerHeading')
    },
    previewText() {
      const parts = [this.contextCourseLabel, this.modeLabel]
      if (this.previewState === 'blocked') return parts.join(' · ')
      if (this.intent === 'learn') {
        if (this.topicValue) parts.push(this.topicValue)
      }
      if (this.trailingSummary) parts.push(this.trailingSummary)
      return parts.join(' · ')
    },
    canContinue() {
      if (!this.intent || !this.readyForCourses) return false
      if (this.loadingCourses || this.coursesError) return false
      if (this.courses.length === 0 || !this.subject) return false
      if (this.subjectUnavailable) return false
      if (this.intent === 'learn' && !this.topicValue) return false
      return true
    },
  },
  watch: {
    gradeOptions: {
      immediate: true,
      handler(options) {
        if (!options.length) return
        if (options.some(o => o.value === this.grade)) return
        // The student's own grade first (from the profile), else the stage's
        // first grade -- V2 opens with a grade already selected.
        const own =
          this.profile && this.profile.educationGrade
            ? this.profile.educationGrade.name
            : ''
        this.grade = options.some(o => o.value === own) ? own : options[0].value
      },
    },
    courseQueryKey: {
      immediate: true,
      handler(key) {
        if (key) {
          this.fetchCourses()
        } else {
          this.courses = []
          this.coursesError = false
        }
      },
    },
    subject() {
      // The suggestion list belongs to a course.
      this.suggestedTopic = ''
      if (this.topicMode === 'select') this.topicMode = ''
      this.loadOutline()
    },
    intent() {
      this.loadOutline()
    },
    submitError(err) {
      // 36 TC-2: the picked chapter is no longer part of this Student's
      // outline -- the choice is cleared (typing a topic stays available) and
      // the outline is read again.
      if (err && err.code === TOPIC_NOT_IN_OUTLINE) {
        if (this.topicMode === 'select') {
          this.suggestedTopic = ''
          this.topicMode = ''
        }
        this.outlineCourse = ''
        this.outlineChapters = []
        this.loadOutline()
      }
      if (
        err &&
        NOT_PROVISIONED_CODES.includes(err.code) &&
        this.lastSubmittedSubject
      ) {
        // SX-03B safety net: a stale "available" that the backend's
        // fail-closed mapping check refused becomes the same Coming Soon UX
        // as a catalog-unavailable course -- badge, blocked summary,
        // disabled CTA and the shared dialog, never the generic failure.
        this.unavailableSubject = this.lastSubmittedSubject
        if (!this.refusedSubjects.includes(this.lastSubmittedSubject)) {
          this.refusedSubjects.push(this.lastSubmittedSubject)
        }
        this.openCourseComingSoon(this.lastSubmittedSubject)
      }
    },
  },
  created() {
    this.fetchReference()
    this.loadProfile()
    // 36 TC-2: a Setup reopened on Learn with its course already chosen.
    this.loadOutline()
  },
  methods: {
    async fetchReference() {
      this.loadingReference = true
      try {
        const data = await StudentService.getEducationReference()
        this.stages = Array.isArray(data.stages) ? data.stages : []
        this.referenceError = false
      } catch (e) {
        this.referenceError = true
      } finally {
        this.loadingReference = false
      }
    },
    async loadProfile() {
      this.profileStatus = 'loading'
      try {
        this.profile = await StudentService.getProfile()
        this.profileStatus = 'loaded'
      } catch (e) {
        this.profile = null
        this.profileStatus = 'error'
      }
    },
    // 36 TC-2 (doc 36/01 §3): the chosen course's chapters, Learn only.
    // Best-effort and never blocking: until (or unless) Subway answers with
    // chapters, the select keeps the static suggestions and "write it
    // yourself" is always there. A late answer for another course is dropped.
    async loadOutline() {
      const course = this.subject
      if (this.intent !== 'learn' || !course) return
      if (this.outlineCourse === course && this.outlineChapters.length) return
      const seq = ++this.outlineSeq
      let chapters = []
      try {
        chapters = aiTutorCollaborators.adapter.toCourseChapters(
          await aiTutorCollaborators.api.getCourseOutline(course)
        )
      } catch (e) {
        chapters = []
      }
      if (seq !== this.outlineSeq || course !== this.subject) return
      this.outlineCourse = course
      this.outlineChapters = chapters
      // The options changed: a static pick that is not one of them is gone.
      if (
        this.topicMode === 'select' &&
        !this.topicOptions.some(option => option.value === this.suggestedTopic)
      ) {
        this.suggestedTopic = ''
        this.topicMode = ''
      }
      this.restorePendingTopic()
    },
    // A retried Setup reopens on the topic it sent; when that topic is one of
    // the outline's chapter titles, the chapter itself is selected again.
    restorePendingTopic() {
      const restored = this.pendingRestoredTopic
      if (!restored || !this.outlineActive) return
      this.pendingRestoredTopic = ''
      const chapter = this.outlineChapters.find(
        entry => entry.title === restored
      )
      if (!chapter) return
      this.suggestedTopic = chapter.nodeId
      this.customTopic = ''
      this.topicMode = 'select'
    },
    async fetchCourses() {
      const key = this.courseQueryKey
      if (!key) return
      this.loadingCourses = true
      this.coursesError = false
      try {
        const data = await StudentService.getCourses({
          educationStage: this.stage,
          educationGrade: this.grade,
          studyField: this.studyFieldRequired ? this.studyFieldName : undefined,
        })
        // The student may have changed grade while this was in flight.
        if (key !== this.courseQueryKey) return
        this.courses = Array.isArray(data.courses) ? data.courses : []
        // V2 opens with a subject already selected: keep a still-valid choice,
        // otherwise take the catalog's first AVAILABLE course (SX-03B) --
        // never an unavailable one. Always a REAL course; '' when nothing in
        // this scope is available yet.
        if (!this.availableCourses.some(c => c.name === this.subject)) {
          this.subject = this.availableCourses.length
            ? this.availableCourses[0].name
            : ''
        }
      } catch (e) {
        if (key !== this.courseQueryKey) return
        this.courses = []
        this.subject = ''
        this.coursesError = true
      } finally {
        if (key === this.courseQueryKey) this.loadingCourses = false
      }
    },
    onIntentInput(option) {
      this.intent = option
    },
    onGradeInput(grade) {
      if (grade === this.grade) return
      this.grade = grade
      // The subject list is per grade: re-selected once it loads.
      this.subject = ''
    },
    onSubjectInput(subject) {
      // Belt and braces: an unavailable course can never become the subject.
      const option = this.courseOptions.find(o => o.value === subject)
      if (option && option.unavailable) {
        this.onSubjectUnavailable(subject)
        return
      }
      this.subject = subject
    },
    // SX-03B: explain, do not select. Nothing is emitted, no request starts.
    onSubjectUnavailable(subject) {
      this.openCourseComingSoon(subject)
    },
    openCourseComingSoon(subject) {
      const option = this.courseOptions.find(o => o.value === subject)
      if (!this.$store) return
      this.$store.dispatch('app/comingSoon/show', {
        variant: 'course',
        courseLabel: option ? option.label : subject,
      })
    },
    onSuggestedTopicChange() {
      this.topicMode = this.suggestedTopic ? 'select' : ''
      this.customTopic = ''
    },
    onCustomTopicToggle() {
      this.topicMode = 'custom'
      this.suggestedTopic = ''
      this.$nextTick(() => {
        if (this.$refs.customTopicInput) this.$refs.customTopicInput.focus()
      })
    },
    // P39 N-4: the custom topic is the form's only one-line text field, so
    // Enter in it submitted the setup (implicit submission) and started the
    // session before the level and the first question were chosen. Enter there
    // now does nothing; Continue is the only way to start. Keys inside an IME
    // composition are left to the browser (it never submits on them).
    onCustomTopicEnter(event) {
      if (event.isComposing) return
      event.preventDefault()
    },
    // Profile is the ONLY place the study field changes. It returns here
    // (replace, so history stays a straight line) and this card re-reads it.
    onEditProfile() {
      this.$router.push({
        name: 'ProfileView',
        params: { lang: this.$route.params.lang },
        query: { from: 'ai-tutor', intent: this.intent },
      })
    },
    onSubmit() {
      if (!this.canContinue) return
      this.lastSubmittedSubject = this.subject
      // Presentation-only labels, on their own channel and emitted BEFORE
      // `submit` so the view has them by the time the chat renders. Nothing
      // here reaches `startSession`, `createSession` or any API payload.
      this.$emit('context-labels', {
        courseLabel: this.contextCourseLabel,
        contextPhrase: this.trailingSummary,
      })
      const context = {
        intent: this.intent,
        grade: this.grade,
        // canContinue guarantees a truthy, real catalog course (doc 11 §H).
        subject: this.subject,
        topic:
          this.intent === 'learn' && this.topicValue ? this.topicValue : null,
      }
      // 36 TC-2: the outline chapter the topic was picked from (Subway
      // checks it and stores the provider's title).
      if (this.intent === 'learn' && this.topicValue && this.topicNodeId) {
        context.topicNodeId = this.topicNodeId
      }
      // FINAL CHAT V2: the mode's own sub-choice is the upstream intake
      // answer for that mode (adapter `toTutorRequest`), sent with the
      // conversation's first turn so the tutor does not ask it again.
      if (this.intent === 'troubleshoot') context.stuckReason = this.stuckReason
      if (this.intent === 'learn') {
        context.proficiency = this.proficiency
        // UX2-01 (D-1): the exact user-authored text, trimmed; '' when empty.
        // Stripped again by `startSession` before any snapshot / DTO.
        context.initialQuestion = (this.initialQuestion || '').trim()
      }
      // CAT-1A (doc 20 §6.1 A): the profile's study field is part of the
      // setup the student saw (read-only row above), so it rides along for
      // Borna's creation-time snapshot. Still never edited here.
      const studyField =
        this.profile && this.profile.studyField
          ? this.profile.studyField.name
          : ''
      if (studyField) context.studyField = studyField
      this.$emit('submit', context)
    },
  },
}
</script>

<style lang="scss" scoped>
// Every value below was measured from the RENDERED reference
// (`borna-student-journey-v2.html`, 00_*.md §18) at 1440 / 700 / 820 / 390;
// the rules (fluid vs stepped, which tier flips what) are the reference's own.
// Colours and radii are the shared Borna tokens.

// --- page box ---------------------------------------------------------------
.ai-tutor-setup-card {
  @include borna-page-container($borna-flow-column);
}

// --- back link (`.back-link`) -------------------------------------------------
.ai-tutor-setup__back {
  display: inline-flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 5px;
  margin-bottom: 18px;
  color: $borna-muted;
  font-size: $borna-fs-nav;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: $borna-indigo-600;
    text-decoration: none;
  }
}

// P43-02 (Owner UAT, supersedes the P42-03 rule): Back is «→» in Persian and
// «←» in English; Forward / Continue is «←» in Persian and «→» in English. The
// `arrow` glyph is drawn «←».
// Back mirrors it in the RTL locale; the CTA (forward) mirrors it in English.
html:not(.is-rtl) {
  .ai-tutor-setup__back {
    flex-direction: row;
  }

  .ai-tutor-setup__submit ::v-deep svg {
    transform: scaleX(-1);
  }
}

html.is-rtl .ai-tutor-setup__back ::v-deep svg {
  transform: scaleX(-1);
}

// --- heading block (`.flow-heading`) ------------------------------------------
.ai-tutor-setup__heading-block {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin: 8px 0 22px;
}

.ai-tutor-setup__icon {
  display: grid;
  flex: 0 0 50px;
  place-items: center;
  width: 50px;
  height: 50px;
  border-radius: 16px;
  color: $borna-text;
}

.ai-tutor-setup__heading-text {
  min-width: 0;
}

.ai-tutor-setup__eyebrow {
  color: $borna-indigo-600;
  font-size: $borna-fs-nav;
  font-weight: $borna-fw-extrabold;
}

.ai-tutor-setup__heading {
  margin: 6px 0 7px;
  color: $borna-ink;
  font-size: 25px;
  font-weight: $borna-fw-regular;
  letter-spacing: -0.03em;
  line-height: $borna-lh-base;

  @include borna-phone {
    font-size: 21px;
  }
}

.ai-tutor-setup__lead {
  margin: 0;
  color: $borna-muted;
  font-size: $borna-fs-nav;
  line-height: 1.8;
}

// --- the card (`.flow-card.intent-form`) --------------------------------------
.ai-tutor-setup__card {
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 22px;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-lg;
  background: $borna-surface;
  box-shadow: $borna-shadow-card;

  @include borna-phone {
    gap: 18px;
    padding: 17px;
    border-radius: 20px;
  }
}

// --- profile row (`.intent-profile-context`) ----------------------------------
.ai-tutor-setup__profile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: -4px 0 0;
  padding: 9px 11px;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-md;
  background: $borna-canvas;

  @include borna-compact {
    padding: 8px 9px;
  }
}

.ai-tutor-setup__profile-id {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: $borna-muted;

  ::v-deep svg {
    flex: none;
    color: $borna-indigo-600;
  }

  > span {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    @include borna-compact {
      display: grid;
      gap: 1px;
    }
  }

  strong {
    overflow: hidden;
    color: $borna-ink;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: $borna-muted;
    font-size: $borna-fs-chip;
    white-space: nowrap;

    @include borna-compact {
      font-size: 9px;
    }
  }
}

.ai-tutor-setup__profile-edit {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 4px;
  min-height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: $borna-indigo-900;
  font-size: $borna-fs-chip;
  font-weight: $borna-fw-extrabold;
  cursor: pointer;

  // Above the compact tier the reference draws an EMPTY 15px slot before the
  // label (its desktop `edit` glyph has no path), so the measured button is
  // 65x32 with text only. The slot is kept, the glyph shown only where the
  // reference draws it (below).
  ::v-deep svg {
    visibility: hidden;
  }

  &:hover,
  &:focus-visible {
    background: $borna-indigo-50;
  }

  // Compact tier: icon-only 36px tile.
  @include borna-compact {
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid $borna-indigo-100;
    background: $borna-surface;
    box-shadow: 0 4px 12px rgba(49, 75, 214, 0.08);
    font-size: 0;

    &:hover,
    &:focus-visible {
      background: $borna-surface;
    }

    ::v-deep svg {
      visibility: visible;
      width: 17px;
      height: 17px;
      color: $borna-indigo-600;
    }
  }
}

.ai-tutor-setup__profile-edit-label {
  @include borna-compact {
    display: none;
  }
}

// --- mode panel (`.quick-intent-detection`) -----------------------------------
.ai-tutor-setup__intent-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  border: 1px solid $borna-indigo-100;
  border-radius: 17px;
  background: linear-gradient(135deg, $borna-indigo-50, #fff);
}

.ai-tutor-setup__intent-head {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  > ::v-deep svg {
    flex: none;
    color: $borna-indigo-600;
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  strong {
    font-size: $borna-fs-nav;
  }

  span {
    color: $borna-muted;
    font-size: $borna-fs-chip;
    line-height: 1.8;
  }
}

.ai-tutor-setup__intent-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.ai-tutor-setup__intent-pill {
  min-height: 35px;
  padding: 0 11px;
  border: 1px solid $borna-border;
  border-radius: 10px;
  background: $borna-surface;
  color: $borna-muted;
  font-size: $borna-fs-chip;
  cursor: pointer;

  &--selected {
    border-color: $borna-indigo-600;
    background: $borna-indigo-600;
    color: #fff;
  }
}

// --- question fields (`.academic-question-field`) -----------------------------
.ai-tutor-setup__field {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  > legend {
    display: block;
    float: none;
    width: auto;
    margin: 0 0 10px;
    padding: 0;
    color: $borna-text;
    font-size: 13px;
    font-weight: $borna-fw-extrabold;
    line-height: $borna-lh-base;
  }
}

// --- learn: topic panel (`.topic-field--pro`) ---------------------------------
.ai-tutor-setup__topic {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid $borna-border;
  border-radius: 20px;
  background: linear-gradient(145deg, #fff, #fbfcff);
  box-shadow: 0 7px 20px rgba(7, 20, 71, 0.035);

  @include borna-compact {
    gap: 12px;
    padding: 13px;
    border-radius: 17px;
  }
}

.ai-tutor-setup__topic-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  > div {
    min-width: 0;
  }

  > div > span {
    display: block;
    color: $borna-ink;
    font-size: 13px;
    font-weight: $borna-fw-extrabold;
    line-height: 1.7;
  }

  > div > small {
    display: block;
    margin-top: 2px;
    color: $borna-muted;
    font-size: $borna-fs-chip;
    line-height: 1.85;
  }

  @include borna-compact {
    display: grid;
    gap: 8px;
  }
}

.ai-tutor-setup__topic-chip {
  flex: none;
  padding: 6px 9px;
  border: 1px solid $borna-indigo-100;
  border-radius: $borna-radius-pill;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font-size: $borna-fs-marker;
  font-weight: $borna-fw-extrabold;
  white-space: nowrap;

  @include borna-compact {
    justify-self: start;
  }
}

.ai-tutor-setup__topic-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-items: stretch;

  @include borna-compact {
    grid-template-columns: 1fr;
    gap: 9px;
  }
}

.ai-tutor-setup__topic-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  min-width: 0;
  margin: 0;
  padding: 14px;
  border: 1.5px solid $borna-border;
  border-radius: $borna-radius-card;
  background: $borna-surface;
  transition: border-color 0.16s, box-shadow 0.16s, background 0.16s;

  @include borna-compact {
    padding: 13px;
  }

  &--active {
    border-color: $borna-indigo-300;
    background: linear-gradient(145deg, #fff, $borna-indigo-50);
    box-shadow: 0 8px 22px rgba(49, 75, 214, 0.08);
  }

  > strong {
    color: $borna-ink;
    font-size: $borna-fs-nav;
    line-height: 1.7;
  }

  > small {
    min-height: 32px;
    color: $borna-muted;
    font-size: $borna-fs-marker;
    line-height: 1.75;

    @include borna-compact {
      min-height: 0;
    }
  }
}

.ai-tutor-setup__topic-kicker {
  color: $borna-indigo-600;
  font-size: $borna-fs-marker;
  font-weight: $borna-fw-extrabold;
  line-height: 1.6;
}

.ai-tutor-setup__topic-control {
  box-sizing: border-box;
  width: 100%;
  height: 46px;
  margin-top: auto;
  padding: 0 12px;
  border: 1.5px solid $borna-border;
  border-radius: $borna-radius-md;
  outline: 0;
  background: $borna-surface;
  color: $borna-text;
  font-size: $borna-fs-input;

  &:hover {
    border-color: $borna-indigo-300;
  }

  &:focus {
    border-color: $borna-indigo-600;
    box-shadow: 0 0 0 4px $borna-indigo-50;
  }

  &:focus-visible {
    outline: 3px solid #61dce8;
    outline-offset: 2px;
  }

  &::placeholder {
    color: #8b96ae;
    opacity: 1;
  }

  @include borna-compact {
    height: 48px;
    min-height: 48px;
    font-size: $borna-fs-nav;
  }
}

select.ai-tutor-setup__topic-control {
  cursor: pointer;
}

.ai-tutor-setup__topic-toggle {
  box-sizing: border-box;
  width: 100%;
  min-height: 46px;
  margin-top: auto;
  padding: 0 12px;
  border: 1px dashed $borna-indigo-300;
  border-radius: $borna-radius-md;
  background: $borna-surface;
  color: $borna-indigo-900;
  font-size: $borna-fs-input;
  font-weight: $borna-fw-extrabold;
  cursor: pointer;
  transition: 0.16s ease;

  &:hover {
    border-style: solid;
    background: $borna-indigo-50;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid #61dce8;
    outline-offset: 2px;
  }

  @include borna-compact {
    min-height: 48px;
    font-size: $borna-fs-nav;
  }
}

// --- proficiency note (`.level-behavior-note`) --------------------------------
// UX2-01: the optional initial-question field under the Learn choices.
.ai-tutor-setup__question {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.ai-tutor-setup__question-label {
  color: $borna-text;
  font-size: 13px;
  font-weight: $borna-fw-extrabold;
}

.ai-tutor-setup__question-input {
  height: auto;
  min-height: 84px;
  padding: 10px 12px;
  margin-top: 0;
  font-family: inherit;
  line-height: 1.8;
  resize: vertical;
}

.ai-tutor-setup__question-hint {
  color: $borna-muted;
  font-size: 11px;
}

.ai-tutor-setup__level-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 10px 0 0;
  padding: 10px 11px;
  border-radius: $borna-radius-sm;
  background: $borna-canvas;
  color: $borna-muted;

  ::v-deep svg {
    flex: none;
    color: $borna-indigo-600;
  }

  span {
    font-size: $borna-fs-chip;
    line-height: 1.8;
  }
}

// --- summary (`.context-preview`) ---------------------------------------------
.ai-tutor-setup__preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px;
  border-radius: $borna-radius-nav;
  background: $borna-indigo-50;
  color: $borna-indigo-900;

  > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  strong {
    font-size: $borna-fs-nav;
  }

  span {
    font-size: $borna-fs-chip;
  }

  &--blocked {
    background: $borna-canvas;
    color: $borna-muted;
  }
}

// --- next-step note (`.chat-next-note`) ---------------------------------------
.ai-tutor-setup__next-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -7px 0 0;
  padding: 10px 12px;
  border-radius: $borna-radius-md;
  background: $borna-teal-50;
  color: $borna-teal-700;
  font-size: $borna-fs-input;
  font-weight: $borna-fw-extrabold;
  line-height: 1.8;

  ::v-deep svg {
    flex: none;
  }
}

// --- CTA (`.button--primary.button--full`) ------------------------------------
.ai-tutor-setup__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  margin-top: 18px;
  padding: 0 19px;
  border: 1px solid transparent;
  border-radius: $borna-radius-nav;
  background: $borna-indigo-600;
  color: #fff;
  font-size: $borna-fs-nav;
  font-weight: $borna-fw-extrabold;
  box-shadow: 0 8px 20px rgba(49, 75, 214, 0.22);
  cursor: pointer;
  transition: transform 0.16s, background 0.16s, border-color 0.16s;

  &:hover {
    background: $borna-indigo-900;
  }

  &:active {
    transform: scale(0.985);
  }

  &:disabled {
    opacity: 0.42;
    box-shadow: none;
    transform: none;
    cursor: not-allowed;
  }
}

// --- "not active yet" note (`.academic-availability-note`) --------------------
.ai-tutor-setup__availability {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 10px 0 0;
  padding: 12px 13px;
  border: 1px solid $borna-indigo-100;
  border-radius: $borna-radius-nav;
  background: linear-gradient(135deg, $borna-indigo-50, #ecfbfe);
  color: $borna-indigo-900;
  font-size: 13px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.8;

  ::v-deep svg {
    flex: none;
    margin-top: 2px;
    color: $borna-teal-700;
  }
}

// --- states the reference does not draw (loading / error) ---------------------
.ai-tutor-setup__hint {
  margin: 0;
  color: $borna-muted;
  font-size: $borna-fs-nav;

  &--inline {
    margin: 8px 2px 0;
  }
}

.ai-tutor-setup__error {
  margin: 0;
  color: $borna-error-text;
  font-size: $borna-fs-nav;

  &--inline {
    margin: 8px 2px 0;
  }
}

.ai-tutor-setup__error-block {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ai-tutor-setup__retry {
  padding: 0;
  border: 0;
  background: none;
  color: $borna-indigo-600;
  font-size: $borna-fs-nav;
  font-weight: $borna-fw-bold;
  cursor: pointer;
}
</style>
